import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import { createERMRouter } from '@adornis/entity-relationship-management/client/erm-router.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { platformRoleManagementErmPage } from '@nsc/nexus-permissions/client/erm-shared-admin.js';
import {
  getPlatformUserById,
  subscribePlatformRoleAssignmentFor,
  subscribePlatformUsersPaginated,
} from '@nsc/nexus-permissions/nexus-api/platform-gated-reads.js';
import {
  hasAnyNexusPermission,
  hasNexusPermission,
} from '@nsc/nexus-permissions/nexus-api/nexus-permission-gate.js';
import { NexusPermission } from '@nsc/nexus-permissions/platform-rbac/nexus-permissions.js';
import { getNexusRoleManagementPermissions } from '@nsc/nexus-permissions/platform-rbac/platform-admin-permissions.js';
import { isNexusRoleManagementRoleName } from '@nsc/nexus-permissions/platform-rbac/platform-role-name-filters.js';
import ArticleGenerationPage from '@nsc/otrs/client/ArticleGenerationPage.js';
import { RoutePlanningsPage } from '@nsc/route-optimization/client/RoutePlanningsPage.js';
import { routePlanningRouter } from '@nsc/route-optimization/client/router.js';
import { TicketStatsPage } from '@nsc/route-optimization/client/TicketStatsPage.js';
import IntrospectionPage from '@nsc/ticket-worker/client/IntrospectionPage.js';
import { WhiteListPage } from '@nsc/whitelist/client/WhiteListPage.js';
import { html, type TemplateResult } from 'lit';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { sendNexusUserInviteEmail } from '../api/nexus-invite-email.js';
import { nexusAuthenticationSection } from './components/NexusAuthenticationSection.js';
import ClientPage from './components/ClientPage.js';
import { showNexusCreateUserDialog } from './components/CreateUserDialog.js';
import { DashboardOperationsSection, DashboardWhitelistSection } from './components/DashboardClassic.js';
import { DashboardClientOverview } from './components/DashboardClientOverview.js';
import { nexusClientAssignmentSection, nexusRoleSection } from './components/NexusClientAssignmentEditor.js';
import { partnerSchedulingClientAssignmentSection } from './components/PartnerSchedulingClientAssignmentEditor.js';
import { NexusClientWorkspacePage } from './components/NexusClientWorkspacePage.js';
import { PartnerSchedulingClientsPage } from './components/PartnerSchedulingClientsPage.js';
import { PartnerSchedulingImportPage } from './components/PartnerSchedulingImportPage.js';
import { PasswordResetPage } from './components/PasswordResetPage.js';
import { SchedulingDebugPage } from './components/SchedulingDebugPage.js';

/** ERM admin shell; must be a `subRouter` of `/admin*` so it receives `localPath` under `/admin`, not the full app path. */
const nexusAdminErmSubrouter = createERMRouter({
  entities: [],
  adminPages: {
    userManagement: {
      onCreate: async (): Promise<string> => {
        const res = await showNexusCreateUserDialog();
        if (!res?.userId) return '';
        if (res.sendInvite) {
          await guardDialog(() => sendNexusUserInviteEmail(res.userId)(), {
            loadingText: 'Sending password setup email…',
            successText: 'Password setup email sent.',
            errorMessagePrefix: 'Could not send password setup email',
            showLoaderModal: true,
          });
        }
        goTo(`/admin/users/detail/${res.userId}`);
        return res.userId;
      },
      list: {
        operation: subscribePlatformUsersPaginated,
      },
      detail: {
        operation: (entityID: string) => getPlatformUserById(decodeURIComponent(entityID)),
        onDelete: async (user: AdornisUser) => {
          await user.remove();
          goTo('/admin/users');
        },
        sections: [
          { title: 'Profile', fields: [{ fieldName: 'email' }, { fieldName: 'username' }] },
          nexusAuthenticationSection,
          nexusRoleSection,
          nexusClientAssignmentSection,
          partnerSchedulingClientAssignmentSection,
        ],
      },
    },
    roleManagement: platformRoleManagementErmPage(
      {},
      {
        roleNameFilter: isNexusRoleManagementRoleName,
        permissions: getNexusRoleManagementPermissions(),
      },
    ),
  },
});

const Forbidden = () =>
  html`<div class="p-6 max-w-lg mx-auto"><div class="alert alert-error">Access denied.</div></div>`;

const Guarded = stateful(
  (props: {
    permission?: NexusPermission;
    anyOf?: readonly NexusPermission[];
    resourceContext?: string;
    inner: () => TemplateResult;
  }) => {
    const permissions = props.anyOf ?? (props.permission ? [props.permission] : []);
    const user = useObservable(() => currentUser(), []);
    const { value: assignment } = useQuery(
      () =>
        user?._id
          ? subscribePlatformRoleAssignmentFor(user._id)({
              roleIds: 1,
              scope: { contexts: 1, permission: 1 },
            })
          : Promise.resolve(null),
      [user?._id],
    );
    const assignmentKey = assignment ? JSON.stringify(assignment) : '';
    const { value: can, loading } = useQuery(
      () => {
        if (!user?._id || permissions.length === 0) return Promise.resolve(false);
        if (permissions.length === 1) {
          return hasNexusPermission(permissions[0]!, props.resourceContext);
        }
        return hasAnyNexusPermission(permissions, props.resourceContext);
      },
      [permissions.join('\0'), props.resourceContext, user?._id, assignmentKey],
    );
    if (!user?._id || loading) {
      return html`<div class="p-6 flex justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>`;
    }
    return can ? props.inner() : Forbidden();
  },
);

export const router = makeRouter({
  dashboard: {
    path: '/',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.ClientView,
        inner: () => html`${DashboardClientOverview()} ${DashboardWhitelistSection()} ${DashboardOperationsSection()}`,
      })}`,
  },
  'client-workspace': {
    path: '/clients/:clientId',
    render: (params: Record<string, string | undefined>) => {
      const clientId = params.clientId ?? '';
      return html`${Guarded({
        permission: NexusPermission.ClientView,
        resourceContext: clientId || undefined,
        inner: () => html`${NexusClientWorkspacePage({ clientId })}`,
      })}`;
    },
  },
  'route-planning': {
    path: '/route-planning',
    subRouter: routePlanningRouter,
    render: () =>
      html`${Guarded({
        permission: NexusPermission.RoutePlanning,
        inner: () => html`${routePlanningRouter.outlet.value}`,
      })}`,
  },
  'route-planning-ticket-stats': {
    path: '/route-planning/ticket-stats',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.ClientStats,
        inner: () => html`${TicketStatsPage()}`,
      })}`,
  },
  'route-planning-plannings': {
    path: '/route-planning/plannings',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.RoutePlanning,
        inner: () => html`${RoutePlanningsPage()}`,
      })}`,
  },
  client: {
    path: '/client',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.ClientManage,
        inner: () => html`${ClientPage()}`,
      })}`,
  },
  introspection: {
    path: '/introspection',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.IntrospectionView,
        inner: () => html`${IntrospectionPage()}`,
      })}`,
  },
  whitelist: {
    path: '/whitelist',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.WhitelistManage,
        inner: () => html`${WhiteListPage()}`,
      })}`,
  },
  articleGeneration: {
    path: '/article-generation',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.ArticleGeneration,
        inner: () => html`${ArticleGenerationPage()}`,
      })}`,
  },
  'scheduling-debug': {
    path: '/scheduling-debug',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.Scheduling,
        inner: () => html`${SchedulingDebugPage()}`,
      })}`,
  },
  'partner-scheduling-import': {
    path: '/partner-scheduling/import',
    render: () =>
      html`${Guarded({
        permission: NexusPermission.PartnerSchedulingImport,
        inner: () => html`${PartnerSchedulingImportPage()}`,
      })}`,
  },
  'partner-scheduling-clients': {
    path: '/partner-scheduling/clients',
    render: () =>
      html`${Guarded({
        anyOf: [NexusPermission.PartnerSchedulingClientToggle, NexusPermission.PartnerSchedulingImport],
        inner: () => html`${PartnerSchedulingClientsPage()}`,
      })}`,
  },
  admin: {
    path: '/admin*',
    subRouter: nexusAdminErmSubrouter,
    render: () =>
      html`${Guarded({
        permission: NexusPermission.UserManage,
        inner: () =>
          html`<div class="min-h-0 h-full flex-1 overflow-auto">${nexusAdminErmSubrouter.outlet.value}</div>`,
      })}`,
  },
  'change-password': {
    path: '/change-password',
    render: () => PasswordResetPage(),
  },
}).init();
